import { createFileRoute } from '@tanstack/react-router'
import { Link, useTranslation } from '@ufc/core'
import { ArrowLeft, Button, Card, CardContent, CardHeader, CardTitle } from '@ufc/uikit'

export const Route = createFileRoute('/privacy-policy')({
  component: PrivacyPolicyPage,
})

const PRIVACY_POLICY_LAST_UPDATED = 'February 11, 2026'

function PrivacyPolicyPage() {
  const { t } = useTranslation()
  return (
    <div className="safe-top relative min-h-screen overflow-hidden">
      {/* Background gradients */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary/30 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-accent/20 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="mx-auto w-full max-w-3xl px-4 py-10">
        <div className="mb-6">
          <Button asChild variant="ghost" className="glass">
            <Link to="/login">
              <ArrowLeft className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2 rtl:rotate-180" />
              {t('privacyPolicy.backToSignIn')}
            </Link>
          </Button>
        </div>

        <Card className="glass-card border-white/10">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">{t('privacyPolicy.title')}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('privacyPolicy.lastUpdated')} {PRIVACY_POLICY_LAST_UPDATED}
            </p>
          </CardHeader>
          <CardContent>
            <article
              lang="en"
              dir="ltr"
              className="space-y-6 text-sm leading-6 text-foreground"
            >
              <section className="space-y-2">
                <h2 className="text-base font-semibold">1. Overview</h2>
                <p>
                  This Privacy Policy describes how United Feed Manufacturing Company
                  (&ldquo;United Feed&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or
                  &ldquo;our&rdquo;) collects, uses, shares, and protects information
                  in connection with the UFC Customer Portal application (the
                  &ldquo;Portal&rdquo;).
                </p>
                <p>
                  The Portal is intended for authorized business users to access
                  account services, including viewing and managing orders, deliveries,
                  invoices, and related information.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold">2. Information We Collect</h2>
                <p>
                  Depending on how you use the Portal, we may collect the following
                  categories of information:
                </p>
                <ul className="list-disc ps-5 space-y-1">
                  <li>
                    <span className="font-medium">Account and contact information</span>
                    , such as your name, email address, phone number, company name,
                    and account identifiers.
                  </li>
                  <li>
                    <span className="font-medium">Authentication and security data</span>
                    , such as information needed to sign in and to help protect the
                    Portal from fraud, abuse, and unauthorized access.
                  </li>
                  <li>
                    <span className="font-medium">Service and transaction data</span>
                    , such as orders, shipments, deliveries, invoices, and other
                    records you view or submit through the Portal.
                  </li>
                  <li>
                    <span className="font-medium">Device and usage data</span>
                    , such as device type, operating system, browser or app version,
                    IP address, log events, and usage patterns.
                  </li>
                  <li>
                    <span className="font-medium">Local storage and similar technologies</span>
                    , such as language preferences and consent choices stored in your
                    browser or device to support Portal functionality.
                  </li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold">3. How We Use Information</h2>
                <p>We use information to:</p>
                <ul className="list-disc ps-5 space-y-1">
                  <li>Provide and operate the Portal and its features.</li>
                  <li>Authenticate users and maintain account security.</li>
                  <li>Process and display service-related information you request.</li>
                  <li>
                    Monitor performance, troubleshoot issues, and improve the Portal.
                  </li>
                  <li>
                    Comply with applicable legal, regulatory, and audit requirements.
                  </li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold">4. Cookies and Analytics</h2>
                <p>
                  The Portal may use cookies and similar technologies (including local
                  storage) for essential functions such as maintaining your session,
                  remembering preferences, and helping secure the Portal. Where
                  applicable, optional analytics are managed through your consent
                  choices in the Portal.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold">5. Sharing and Disclosure</h2>
                <p>
                  We may share information with service providers that help us operate
                  the Portal (for example, hosting, security, analytics, and customer
                  support). We may also disclose information where required by law,
                  regulation, or a valid legal process, or to protect the rights,
                  safety, and security of United Feed, our users, or others.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold">6. Data Retention</h2>
                <p>
                  We retain information for as long as necessary to provide the Portal
                  and fulfill the purposes described in this Privacy Policy, unless a
                  longer retention period is required or permitted by law.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold">7. Security</h2>
                <p>
                  We maintain administrative, technical, and organizational safeguards
                  designed to protect information. However, no method of transmission
                  over the Internet or method of electronic storage is completely
                  secure.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold">8. Your Choices and Rights</h2>
                <p>
                  Depending on applicable law, you may have rights to access, correct,
                  update, or request deletion of certain information, and to manage
                  your consent preferences for optional analytics. To request support,
                  please contact United Feed through the official support channels available
                  to you.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold">9. Updates to This Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. The
                  &ldquo;Last updated&rdquo; date above indicates when changes were
                  most recently made.
                </p>
              </section>
            </article>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
